import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useData } from '../../context/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUploadCloud, FiFileText, FiCheckCircle, FiAlertCircle, FiX, FiLoader, FiInfo } = FiIcons;

const ExcelImporter = ({ isOpen, onClose }) => {
  const { saveInventoryData, isSyncing } = useData();
  const [status, setStatus] = useState('idle'); // idle, reading, uploading, success, error
  const [error, setError] = useState(null);
  const [fileDetails, setFileDetails] = useState(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setError(null);
      setFileDetails(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setStatus('reading');
    setError(null);
    setFileDetails({
      name: file.name,
      size: (file.size / 1024).toFixed(2) + ' KB'
    });

    try {
      // 1. Extracción de Metadatos
      let extractedDate = null;
      const dateMatch = file.name.match(/^(\d{8})_/);
      if (dateMatch) {
        const rawDate = dateMatch[1];
        extractedDate = `${rawDate.substring(6, 8)}/${rawDate.substring(4, 6)}/${rawDate.substring(0, 4)}`;
      }

      const importMetadata = {
        filename: file.name,
        extracted_date: extractedDate,
        upload_timestamp: new Date().toISOString()
      };

      // 2. Lectura del Archivo Excel
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
          try {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: 'binary' });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            // Convertimos a JSON crudo
            resolve(XLSX.utils.sheet_to_json(ws));
          } catch (err) {
            reject(new Error('Formato de Excel no válido'));
          }
        };
        reader.onerror = () => reject(new Error('Error al leer el archivo físico'));
        reader.readAsBinaryString(file);
      });

      if (!data || data.length === 0) {
        throw new Error('El archivo Excel está vacío');
      }

      // 3. Sincronización Directa (ELIMINADO EL MAPEO MANUAL QUE CORROMPIA DATOS)
      setStatus('uploading');
      
      // Enviamos el JSON crudo. El DataContext se encargará de buscar las columnas dinámicamente.
      await saveInventoryData(data, importMetadata);

      setStatus('success');
      setTimeout(() => {
        onClose();
        setStatus('idle');
      }, 1500);

    } catch (err) {
      console.error('Import Error:', err);
      setError(err.message || 'Error desconocido durante la importación');
      setStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="p-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Importar Datos</h3>
            <p className="text-xs text-gray-500">Actualización masiva de inventario</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <SafeIcon icon={FiX} className="text-gray-400" />
          </button>
        </div>

        <div className="p-8">
          {status === 'idle' && (
            <div className="space-y-4">
              <label className="border-2 border-dashed border-gray-200 rounded-3xl p-10 flex flex-col items-center justify-center space-y-4 hover:border-red-200 hover:bg-red-50/30 transition-all cursor-pointer group relative overflow-hidden">
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".xlsx,.xls" 
                  onChange={handleFileUpload}
                  disabled={isSyncing}
                />
                <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiUploadCloud} className="text-3xl text-red-500" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-gray-700">Seleccionar Reporte Excel</p>
                  <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider font-bold">Clic para abrir explorador</p>
                </div>
              </label>

              <div className="bg-blue-50 rounded-2xl p-4 flex items-start space-x-3 border border-blue-100">
                <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5 shrink-0" />
                <p className="text-[11px] text-blue-700 leading-relaxed">
                  Para versionado automático, use el formato: <br/>
                  <span className="font-mono font-bold text-blue-800">YYYYMMDD_ID_AllGarments_Report.xlsx</span>
                </p>
              </div>
            </div>
          )}

          {(status === 'reading' || status === 'uploading') && (
            <div className="py-10 text-center space-y-6">
              <div className="relative w-20 h-20 mx-auto">
                <div className="absolute inset-0 border-4 border-gray-100 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-red-600 rounded-full border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <SafeIcon icon={FiLoader} className="text-red-600 animate-pulse" />
                </div>
              </div>
              <div>
                <p className="text-sm font-black text-gray-800 uppercase tracking-tight">
                  {status === 'reading' ? 'Analizando Archivo...' : 'Sincronizando Nube...'}
                </p>
                {fileDetails && (
                  <p className="text-[10px] text-gray-400 mt-1 font-mono">{fileDetails.name}</p>
                )}
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="py-10 text-center space-y-4 animate-in zoom-in duration-300">
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto shadow-inner">
                <SafeIcon icon={FiCheckCircle} className="text-4xl text-green-500" />
              </div>
              <p className="text-sm font-bold text-gray-800">¡Datos actualizados!</p>
              <p className="text-xs text-gray-500">La vista se refrescará automáticamente.</p>
            </div>
          )}

          {status === 'error' && (
            <div className="py-10 text-center space-y-6">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto">
                <SafeIcon icon={FiAlertCircle} className="text-4xl text-red-500" />
              </div>
              <div>
                <p className="text-sm font-bold text-red-600">Error de Importación</p>
                <p className="text-xs text-gray-500 mt-2 px-4 line-clamp-2">{error}</p>
              </div>
              <button 
                onClick={() => setStatus('idle')}
                className="px-8 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-bold transition-all active:scale-95 shadow-lg"
              >
                Intentar de nuevo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelImporter;